import { type PropsWithChildren, useCallback } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useLocale, useOpenWindow } from '@/hooks';
import { BoltIcon, PaperIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { BasicPricingCard, FreePricingCard, ProPricingCard } from '@/shared/components/PricingCard';
import { useCountryCode } from '@/shared/hooks';
import { Button } from '@/ui_v3';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { usePaymentFlow } from '../shared';
import { getPaymentLoginUrl } from '../shared/utils/paymentEntryUrl';
import { SchoolBudgetBanner } from './SchoolBudgetBanner';
export const PlanExplanation = () => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    isAuthenticated
  } = useUser();
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const {
    locale
  } = useLocale();
  const isKoKR = isKR && locale === 'ko';
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const openWindow = useOpenWindow();
  const handleClickComparePlanButton = useCallback(() => {
    logger.track('click_compare_plan_features_button');
    const TARGET_ELEMENT_ID = 'plan-comparison';
    if (router.pathname === '/pricing') {
      document.getElementById(TARGET_ELEMENT_ID)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    } else {
      void router.push(`/pricing#${TARGET_ELEMENT_ID}`);
    }
  }, [router]);
  return <div className="flex w-full flex-col items-center gap-xl" data-sentry-component="PlanExplanation" data-sentry-source-file="PlanExplanation.tsx">
      <div className="flex flex-col items-center gap-lg">
        <h2 className="whitespace-pre-line text-center text-display-sm-extrabold">
          <Trans t={t} i18nKey="common:pricing.enhanceClassWithMoreFeatures" components={{
          Gradient: <GradientText />
        }} data-sentry-element="Trans" data-sentry-source-file="PlanExplanation.tsx" />
        </h2>
        <div className="flex flex-col gap-sm tablet-sm:flex-row-reverse">
          {isKR && <>
              <GetQuoteButton />
              <ComparePlanFeatureButton onClick={handleClickComparePlanButton} />
            </>}
        </div>
      </div>
      {isKoKR && <SchoolBudgetBanner />}
      <div className="flex w-full max-w-[1280px] flex-col items-center gap-lg tablet-sm:flex-row tablet-sm:items-stretch tablet-sm:justify-center">
        <FreePricingCard onCtaButtonClick={() => {
        logger.track('click_pricing_card_cta', {
          planType: 'FREE',
          from: router.pathname
        });
        void router.push(isAuthenticated ? '/build' : combinePathAndQuery('/login', router.query));
      }} data-sentry-element="FreePricingCard" data-sentry-source-file="PlanExplanation.tsx" />
        <BasicPricingCard onCtaButtonClick={() => {
        logger.track('click_pricing_card_cta', {
          planType: 'BASIC',
          from: router.pathname
        });
        if (!isAuthenticated) {
          openWindow(getPaymentLoginUrl('BASIC', countryCode));
          return;
        }
        startPaymentFlow({
          plan: 'BASIC',
          countryCode: countryCode
        }, {
          target: 'new-tab'
        });
      }} data-sentry-element="BasicPricingCard" data-sentry-source-file="PlanExplanation.tsx" />
        <ProPricingCard onCtaButtonClick={() => {
        logger.track('click_pricing_card_cta', {
          planType: 'PRO',
          from: router.pathname
        });
        if (!isAuthenticated) {
          openWindow(getPaymentLoginUrl('PRO', countryCode));
          return;
        }
        startPaymentFlow({
          plan: 'PRO',
          countryCode: countryCode
        }, {
          target: 'new-tab'
        });
      }} data-sentry-element="ProPricingCard" data-sentry-source-file="PlanExplanation.tsx" />
      </div>
    </div>;
};
const GradientText = ({
  children
}: PropsWithChildren) => {
  return <strong className="bg-clip-text text-transparent bg-gradient-violet-blue" data-sentry-component="GradientText" data-sentry-source-file="PlanExplanation.tsx">
      {children}
    </strong>;
};
const GetQuoteButton = () => {
  const {
    t
  } = useTranslation(['common']);
  const openWindow = useOpenWindow();
  return <Button intent="strong" size="lg" rounded="full" leftSlot={<PaperIcon size="md" />} onClick={() => {
    logger.track('click_get_quote_button');
    openWindow('/pricing/quote');
  }} data-sentry-element="Button" data-sentry-component="GetQuoteButton" data-sentry-source-file="PlanExplanation.tsx">
      {t('common:pricing.getQuoteInMinute')}
    </Button>;
};
const ComparePlanFeatureButton = ({
  onClick
}: {
  onClick?: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <Button variant="outlined" intent="alternative" size="lg" rounded="full" leftSlot={<BoltIcon size="md" />} onClick={onClick} data-sentry-element="Button" data-sentry-component="ComparePlanFeatureButton" data-sentry-source-file="PlanExplanation.tsx">
      {t('common:pricing.comparePlanFeatures')}
    </Button>;
};